import type {
	ModelContext,
	ProvideContextInput,
	RegisterToolOptions,
	ToolDescriptor,
	ToolResult
} from './browser-mcp.model';

/**
 * In-process tool registry whose surface mirrors the shape of the WebMCP
 * `document.modelContext` API. This is **not** a spec-compliant polyfill:
 * it does not install on `document`, does not implement transport to a model,
 * and does not promise coverage of the WebMCP draft.
 *
 * The intent is to give Angular components in the host app a stable,
 * WebMCP-style entry point (exposed as `window.BrowserMCP`) for registering
 * callbacks that the Agent Harness will later invoke for UI interactions.
 * Transport between the page and the Agent Harness is out of scope here —
 * this class only owns the local registry.
 *
 * Spec reference (shape only, not a conformance target):
 * https://webmachinelearning.github.io/webmcp/
 */
export class BrowserMCP implements ModelContext {
	private static _instance: BrowserMCP;
	private readonly _tools = new Map<string, ToolDescriptor>();

	private constructor() {
		// private to enforce singleton access via getInstance()
	}

	static getInstance(): BrowserMCP {
		if (!BrowserMCP._instance) {
			BrowserMCP._instance = new BrowserMCP();
		}
		return BrowserMCP._instance;
	}

	registerTool(descriptor: ToolDescriptor, options?: RegisterToolOptions): void {
		BrowserMCP.assertValidDescriptor(descriptor);
		if (this._tools.has(descriptor.name)) {
			throw new Error(`BrowserMCP.registerTool: tool "${descriptor.name}" is already registered`);
		}

		this._tools.set(descriptor.name, descriptor);

		const signal = options?.signal;
		if (signal) {
			if (signal.aborted) {
				this._tools.delete(descriptor.name);
				return;
			}
			signal.addEventListener('abort', () => this.unregisterTool(descriptor.name), { once: true });
		}
	}

	unregisterTool(name: string): void {
		this._tools.delete(name);
	}

	/**
	 * Atomically replaces the registry with the given tools. Validates every
	 * descriptor (name, execute, in-batch uniqueness) into a temporary map
	 * before touching `_tools` — if any descriptor fails, the existing
	 * registry is left untouched. AbortSignal-based auto-unregistration is
	 * intentionally not supported here; pass per-tool signals via
	 * `registerTool` if you need that.
	 */
	provideContext(context: ProvideContextInput): void {
		const next = new Map<string, ToolDescriptor>();
		for (const tool of context.tools) {
			BrowserMCP.assertValidDescriptor(tool);
			if (next.has(tool.name)) {
				throw new Error(`BrowserMCP.provideContext: tool "${tool.name}" appears more than once in the batch`);
			}
			next.set(tool.name, tool);
		}

		this._tools.clear();
		for (const [name, tool] of next) {
			this._tools.set(name, tool);
		}
	}

	listTools(): ToolDescriptor[] {
		return Array.from(this._tools.values());
	}

	async callTool(name: string, params: unknown): Promise<ToolResult> {
		const tool = this._tools.get(name);
		if (!tool) {
			throw new Error(`BrowserMCP.callTool: tool "${name}" not found`);
		}
		return await tool.execute(params);
	}

	private static assertValidDescriptor(descriptor: ToolDescriptor): void {
		if (!descriptor || typeof descriptor.name !== 'string' || descriptor.name.trim().length === 0) {
			throw new Error('BrowserMCP: descriptor.name must be a non-empty string');
		}
		if (typeof descriptor.execute !== 'function') {
			throw new Error(`BrowserMCP: tool "${descriptor.name}" must define an execute function`);
		}
	}
}
